import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, RotateCcw, Loader2, X, Check } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, fieldStyle, selectStyle, navyBtn, outlineBtn,
} from '../../styles/tokens';
import client from '../../api/client';
import { useOptions } from '../../context/OptionsContext';
import { usePaginatedSort } from '../../shared/hooks/usePaginatedSort';
import PaginationBar from '../../shared/components/PaginationBar';
import SortableHeader from '../../shared/components/SortableHeader';

export default function CostCenterManagement() {
  const { refresh: refreshOptions } = useOptions();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showAdd, setShowAdd]   = useState(false);
  const [newName, setNewName]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    try {
      const res = await client.get('/api/admin/cost-centers');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to load cost centers:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!newName.trim()) return;
    setSaving(true);
    setError('');
    try {
      await client.post('/api/admin/cost-centers', { name: newName.trim() });
      setNewName('');
      setShowAdd(false);
      await fetchItems();
      refreshOptions();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear');
    } finally {
      setSaving(false);
    }
  }

  async function handleRename(id) {
    if (!editName.trim()) return;
    try {
      await client.patch(`/api/admin/cost-centers/${id}`, { name: editName.trim() });
      setEditId(null);
      await fetchItems();
      refreshOptions();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al renombrar');
    }
  }

  async function handleDelete(id) {
    try {
      await client.delete(`/api/admin/cost-centers/${id}`);
      await fetchItems();
      refreshOptions();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  }

  async function handleReactivate(id) {
    try {
      await client.patch(`/api/admin/cost-centers/${id}`, { active: true });
      await fetchItems();
      refreshOptions();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al reactivar');
    }
  }

  const term = search.toLowerCase();
  const filteredItems = items.filter(i => {
    if (!i.name.toLowerCase().includes(term)) return false;
    if (statusFilter === 'active') return i.active;
    if (statusFilter === 'inactive') return !i.active;
    return true;
  });
  const activeCount = items.filter(i => i.active).length;

  // V29.2: hook MUST run on every render (before any early return) so hook order stays stable
  const {
    pageItems, page, pageSize, totalPages, totalItems,
    sortKey, sortDir, toggleSort, setPage, setPageSize,
  } = usePaginatedSort(filteredItems, { defaultSortKey: 'name' });

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando centros de costo…
      </div>
    );
  }

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>
          Centros de Costo
          <span style={{ fontSize: 13, fontWeight: '400', color: '#94a3b8', marginLeft: 8 }}>
            ({activeCount} activos)
          </span>
        </h2>
        <button onClick={() => { setShowAdd(true); setError(''); setNewName(''); }} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Agregar Centro de Costo
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar código..."
          style={{ ...fieldStyle, width: 240 }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...selectStyle, width: 'auto' }}>
          <option value="active">Activos</option>
          <option value="inactive">Inactivos</option>
          <option value="all">Todos</option>
        </select>
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ ...bubbleStyle, marginBottom: 20, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Nombre del Centro de Costo</label>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                style={fieldStyle}
                placeholder="ej., Investigación y Desarrollo"
                autoFocus
              />
            </div>
            <button onClick={handleAdd} disabled={saving} style={{ ...navyBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Agregando…' : 'Agregar'}
            </button>
            <button onClick={() => setShowAdd(false)} style={outlineBtn}>Cancelar</button>
          </div>
          {error && (
            <div style={{
              marginTop: 8, padding: '6px 12px', background: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca', borderRadius: 6, fontSize: 12,
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Items table */}
      <div style={bubbleStyle}>
        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 80px 80px',
          gap: 8, padding: '0 0 12px', borderBottom: `2px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <SortableHeader label="Nombre" sortKey="name"   currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <SortableHeader label="Estado" sortKey="active" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
          <span style={{ textAlign: 'right' }}>Acciones</span>
        </div>

        {/* Rows */}
        {pageItems.map(item => (
          <div key={item.id} style={{
            display: 'grid', gridTemplateColumns: '1fr 80px 80px',
            gap: 8, alignItems: 'center',
            padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            opacity: item.active ? 1 : 0.6, fontSize: 13,
          }}>
            {editId === item.id ? (
              <>
                <input
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleRename(item.id)}
                  style={{ ...fieldStyle, fontSize: 13, padding: '4px 8px' }}
                  autoFocus
                />
                <span />
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleRename(item.id)} style={iconBtn} title="Guardar">
                    <Check size={14} style={{ color: '#16a34a' }} />
                  </button>
                  <button onClick={() => setEditId(null)} style={iconBtn} title="Cancelar">
                    <X size={14} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <span style={{
                  fontWeight: '500',
                  color: item.active ? NAVY : '#94a3b8',
                  textDecoration: item.active ? 'none' : 'line-through',
                }}>{item.name}</span>
                <span>
                  <span style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                    fontSize: 11, fontWeight: '600',
                    background: item.active ? '#f0fdf4' : '#fef2f2',
                    color: item.active ? '#166534' : '#991b1b',
                  }}>
                    {item.active ? 'Activo' : 'Inactivo'}
                  </span>
                </span>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  {item.active ? (
                    <>
                      <button onClick={() => { setEditId(item.id); setEditName(item.name); }} style={iconBtn} title="Editar">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} style={iconBtn} title="Desactivar">
                        <Trash2 size={13} style={{ color: '#dc2626' }} />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => handleReactivate(item.id)} style={iconBtn} title="Reactivar">
                      <RotateCcw size={13} style={{ color: '#16a34a' }} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No se encontraron centros de costo
          </div>
        )}
      </div>

      <PaginationBar
        page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems}
        onPageChange={setPage} onPageSizeChange={setPageSize}
      />
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 4,
};

const iconBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
